import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { getErrorMessage } from "@/lib/errorUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent double call in React StrictMode
      if (hasVerified.current) return;
      hasVerified.current = true;

      const token = searchParams.get("token");
      if (!token) {
        setStatus("error");
        setMessage("Token non valido");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus("success");
        setMessage(response.data.message);
      } catch (error) {
        setStatus("error");
        setMessage(getErrorMessage(error, "Errore nella verifica dell'email"));
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="verify-email-page">
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
      </div>

      <motion.div
        className="glass-card p-8 max-w-md w-full text-center relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {status === "loading" && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#fcb900]" />
            <p className="mt-4 text-muted-foreground">Verifica in corso...</p>
          </div>
        )}

        {status === "success" && (
          <div className="py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#fcb900]/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-[#fcb900]" />
            </div>
            <h1 className="text-2xl font-semibold mb-2">Email Verificata!</h1>
            <p className="text-muted-foreground mb-6" data-testid="verify-success-message">{message}</p>
            <Link to="/">
              <Button className="rounded-full btn-gold" data-testid="go-to-login-btn">
                Vai al Login
              </Button>
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#fc0301]/20 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-[#fc0301]" />
            </div>
            <h1 className="text-2xl font-semibold mb-2">Errore</h1>
            <p className="text-muted-foreground mb-6" data-testid="verify-error-message">{message}</p>
            <Link to="/">
              <Button className="rounded-full btn-gold" data-testid="go-to-home-btn">
                Torna alla Home
              </Button>
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
